import React from 'react';
import {
  Box,
  CircularProgress,
  Fade,
  Typography,
} from '@mui/material';
import { Formik, Form } from 'formik';
import { useHistory } from 'react-router-dom';
import type { PLRFormProps } from '../../../../../../types/preloan';
import {
  getContactMessage, firstMessageSwitch,
  secondMessageSwitch, getRedirectUrl,
} from './components/messageSwitches';
import type { FormSchemaV3 } from '../../../../../../types/preloan/v3';
import analytics from '../../../../../../utils/analytics';
import { getOriginButtonLabel, isMortgageFlow } from '../../formLogic';
import HTMLParser from '../../../../../../components/HTMLParser';
import ComplementLeadResultDialog from '../../../ComplementInvite/ComplementLeadResultDialog';
import {
  inviteePlrCategory,
  LEAD_OFFER_PAGE,
  leadResultCategory,
} from '../../../ComplementInvite/analyticsPages';
import CandidateDialog from '../../../ComplementInvite/CandidateDialog';
import PlrShell, { partnerDisplayName } from '../../../landing/PlrShell';
import { PlrTitle, ProjectLabel } from '../../../landing/ui';
import {
  ComplementCard, DecisionInfo, InfoRow, PreApprovalCard, PreApprovalItem, PreQualifiesChip,
  ResultClosing, SimulationNote,
} from '../../../landing/PlrResults';
import {
  formatPreApproveAmount, htmlToText, isAlreadyTransferred, pickShownPreApproves,
} from '../../../landing/results';
import { partnerFromSchema, useShowHero } from '../../../landing/partner';
import { plr } from '../../../landing/tokens';
import { schemaHasProject, schemaProjectLabel } from '../../../heroVariant';

const MANUAL_VERIFICATION_TEXT = 'No pudimos terminar tu evaluación en línea. Un ejecutivo revisará tus datos y te contactaremos para seguir.';

export default function LastStepForm<T extends FormSchemaV3>(props: PLRFormProps<T>) {
  const { formRef, formSchema, handleEnableLoading } = props;
  const history = useHistory();
  const leadCategory = leadResultCategory(formSchema.isRedirect);
  const alreadyEvaluatedInvitee = Boolean(formSchema.complementToken)
    && formSchema.isRedirect === 'REDIRECCION RUT'
    && typeof formSchema.leadComplementAccepted !== 'boolean';
  const lastStepText = formSchema.config.preLoanRequestConfig?.lastStepText;
  // showPreApprove = false oculta el resultado: tampoco se ofrece complementar la renta.
  // Tampoco si la solicitud ya se transfirió a una mutuaria.
  const showComplementOffer = Boolean(formSchema.complementOfferAvailable)
    && Boolean(lastStepText?.showPreApprove)
    && typeof formSchema.leadComplementAccepted !== 'boolean'
    && !alreadyEvaluatedInvitee
    && !isAlreadyTransferred(formSchema.preApproves);
  const partner = partnerFromSchema(formSchema);
  const showHero = useShowHero(formSchema);
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const mortgageFlow = isMortgageFlow(formSchema.products);
  const [mainPreApprove, ...otherPreApproves] = pickShownPreApproves(formSchema.preApproves);
  React.useEffect(() => handleEnableLoading(false), []);
  React.useEffect(() => {
    const resultCategory = inviteePlrCategory(
      formSchema.complementToken,
      formSchema.isRedirect ? `PLR - ${formSchema.isRedirect}` : 'PLR - RESULTADO',
    );
    const leadCategoryForOffer = leadResultCategory(formSchema.isRedirect);

    if (formSchema.step === 'READY_FOR_LOAN') {
      analytics.page(resultCategory, 'ESPERANDO APROBACIÓN MEDIANTE BACKOFFICE');
      if (showComplementOffer) {
        analytics.page(leadCategoryForOffer, LEAD_OFFER_PAGE, { screen: 'lastStep' });
      }
    }
    if (formSchema.step === 'COMPANY_APROVE') {
      analytics.page(resultCategory, 'ESPERANDO APROBACIÓN DEL EMPLEADOR');
    }
    if (formSchema.step === 'MANUAL_VERIFICATION') {
      analytics.page(resultCategory, 'ESPERANDO APROBACION MANUAL POR EVALUACION INCOMPLETA');
    }
    if (formSchema.origin === 'portal-usuario') {
      history.push('/account');
      return;
    }
    if (formSchema.config.preLoanRequestConfig?.lastStepText?.autoRedirect) {
      setTimeout(() => {
        window.location.href = getRedirectUrl(
          formSchema.config,
          true,
        );
      }, 5000);
    }
  }, []);

  return (
    <Formik
      innerRef={formRef}
      initialValues={formSchema}
      onSubmit={(values) => {
        analytics.track('Click Boton', {
          initiator: 'usuario',
          text: getOriginButtonLabel(values.origin),
        });
        if (values.origin === 'portal-usuario') {
          window.location.assign(`${process.env.REACT_APP_BANCAME_PORTAL_URL}/account`);
          return;
        }
        setTimeout(() => {
          window.location.href = getRedirectUrl(
            values.config,
            true,
          );
        }, 100);
      }}
    >
      {({
        values,
      }) => {
        if (!mortgageFlow) {
          return (
            <Fade
              in
              {...({ timeout: 1000 })}
            >
              <Form>
                <Typography variant="h6" align="center" gutterBottom>
                  {formSchema.config.preLoanRequestConfig?.lastStepText?.header}
                </Typography>
                <Typography
                  style={{ marginTop: 10 }}
                  gutterBottom
                  variant="body1"
                  align="center"
                  component="div"
                  data-pw="preloan-last-step-body"
                >
                  {firstMessageSwitch(
                    {
                      step: values.step,
                      preApproves: formSchema.preApproves,
                      firstMessage: lastStepText?.firstMessage,
                      token: formSchema.PLRtoken,
                      complementOfferAvailable: showComplementOffer,
                      complementOfferPageCategory: leadCategory,
                      verificationPending: values.verificationPending,
                      partner: {
                        partnerName: formSchema.partner,
                        config: formSchema.config,
                        products: formSchema.products,
                      },
                    },
                  )}
                </Typography>
                {formSchema.config.preLoanRequestConfig?.lastStepText?.autoRedirect && (
                <Typography component="div" variant="body2" align="center" style={{ marginTop: 20 }}>
                  Se te redirigirá en breve...
                  {' '}
                  <CircularProgress size={20} />
                </Typography>
                )}
                <Typography
                  style={{ marginTop: 20 }}
                  variant="caption"
                  align="center"
                  component="div"
                  gutterBottom
                >
                  {formSchema.config.preLoanRequestConfig?.lastStepText?.secondMessage && (
                    <HTMLParser
                      html={secondMessageSwitch(
                        values.step,
                        formSchema.config.preLoanRequestConfig?.lastStepText?.secondMessage,
                      ) as string}
                    />
                  )}
                  {' '}
                  {getContactMessage(formSchema.config)}
                </Typography>
                <ComplementLeadResultDialog
                  leadComplementAccepted={formSchema.leadComplementAccepted}
                  ownPreApproved
                  leadFirstName={formSchema.complementLeadFirstName}
                  alreadyEvaluated={alreadyEvaluatedInvitee}
                  partnerName={formSchema.partner}
                />
              </Form>
            </Fade>
          );
        }
        // showPreApprove = false: el partner oculta el resultado. Como el flujo anterior, se
        // muestran sus textos (header, firstMessage, secondMessage), sin chip ni montos.
        const showApproved = values.step === 'READY_FOR_LOAN'
          && Boolean(mainPreApprove)
          && !values.verificationPending
          && Boolean(lastStepText?.showPreApprove);
        const partnerName = partnerDisplayName(partner);
        // Evaluación incompleta (p. ej. sin documentos legibles): la revisa una persona.
        const manualVerification = values.step === 'MANUAL_VERIFICATION';
        // firstMessageSwitch usa un hook (useTheme): se llama siempre, aunque no se muestre.
        const leadMessage = firstMessageSwitch({
          step: values.step,
          preApproves: formSchema.preApproves,
          firstMessage: lastStepText?.firstMessage,
          token: formSchema.PLRtoken,
          complementOfferAvailable: false,
          complementOfferPageCategory: leadCategory,
          verificationPending: values.verificationPending,
          partner,
        });
        const contactIntro = lastStepText?.secondMessage
          ? htmlToText(secondMessageSwitch(values.step, lastStepText.secondMessage) || '')
          : 'Si tienes alguna duda,';
        const contactLine = `${contactIntro}${getContactMessage(formSchema.config)}.`;
        const { contactEmail, contactPhone } = formSchema.config;
        const hasContact = Boolean(contactEmail || contactPhone);
        let content: React.ReactNode;
        if (showApproved && mainPreApprove) {
          content = (
            <>
              <PreApprovalCard preApprove={mainPreApprove} />
              {otherPreApproves.map((preApprove) => (
                <PreApprovalItem key={preApprove.id} preApprove={preApprove} />
              ))}
              <SimulationNote />
              {showComplementOffer && (
                <ComplementCard
                  variant="approved"
                  amountLabel={formatPreApproveAmount(mainPreApprove)}
                  onOpen={() => setPickerOpen(true)}
                />
              )}
              {(lastStepText?.firstMessage || hasContact) && (
                <InfoRow>
                  {lastStepText?.firstMessage && (
                    <Box sx={{ color: plr.ink, fontWeight: 600 }}>
                      <HTMLParser html={lastStepText.firstMessage} />
                    </Box>
                  )}
                  <span>{contactLine}</span>
                </InfoRow>
              )}
            </>
          );
        } else {
          const readyForLoan = values.step === 'READY_FOR_LOAN';
          let lead: React.ReactNode = leadMessage;
          if (manualVerification) {
            lead = MANUAL_VERIFICATION_TEXT;
          } else if (readyForLoan) {
            // Solo el firstMessage del partner: sin la nota "Esta simulación es referencial…"
            // de firstMessageSwitch, que delataba la preaprobación con el resultado oculto.
            lead = lastStepText?.firstMessage
              ? <HTMLParser html={lastStepText.firstMessage} />
              : null;
          }
          content = (
            <>
              <PlrTitle size="result" sx={{ mt: '-6px' }}>
                {manualVerification
                  ? 'Revisaremos tu solicitud'
                  : lastStepText?.header || 'Recibimos tu solicitud'}
              </PlrTitle>
              <Box
                data-pw="preloan-last-step-body"
                sx={{
                  m: '-4px 0 0',
                  fontSize: { xs: 15, sm: 17 },
                  lineHeight: 1.5,
                  color: plr.muted,
                  '& p': { m: 0 },
                  '& p + p': { mt: '8px' },
                  '& strong': { color: plr.ink },
                  // firstMessageSwitch (flujo anterior) usa Typography: que herede este estilo.
                  '& .MuiTypography-root': {
                    font: 'inherit', color: 'inherit', textAlign: 'left', m: 0,
                  },
                  '& .MuiTypography-caption': {
                    mt: '10px', fontSize: { xs: 12, sm: 13 }, lineHeight: 1.45, color: plr.muted,
                  },
                }}
              >
                {lead}
              </Box>
              {/* Como el flujo anterior: la oferta de complemento solo va en READY_FOR_LOAN. */}
              {showComplementOffer && readyForLoan && (
                <ComplementCard variant="approved" onOpen={() => setPickerOpen(true)} />
              )}
              <Box component="p" sx={{ m: 0, fontSize: { xs: 13, sm: 14 }, color: plr.body }}>
                {contactLine}
              </Box>
            </>
          );
        }
        return (
          <Form style={{ width: '100%' }}>
            <PlrShell
              partner={partner}
              showHero={showHero}
              contentMaxWidth={560}
              testId="plr-result"
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: '10px', sm: '12px' } }}>
                {showHero && (
                  <ProjectLabel hideOnPanel>
                    {schemaProjectLabel(formSchema)}
                  </ProjectLabel>
                )}
                {showApproved && <PreQualifiesChip />}
              </Box>
              {content}
              {lastStepText?.autoRedirect && (
                <Box
                  component="p"
                  sx={{
                    m: 0, display: 'flex', alignItems: 'center', gap: '8px', fontSize: 14, color: plr.body,
                  }}
                >
                  Se te redirigirá en breve...
                  <CircularProgress size={16} sx={{ color: plr.ink }} />
                </Box>
              )}
              {showApproved && (
                <DecisionInfo
                  project={schemaProjectLabel(formSchema)}
                  hasProject={schemaHasProject(formSchema)}
                />
              )}
              <ResultClosing backLabel={`Volver a ${partnerName}`} />
            </PlrShell>
            <CandidateDialog
              open={pickerOpen}
              token={formSchema.PLRtoken}
              pageCategory={leadCategory}
              onClose={() => setPickerOpen(false)}
            />
            <ComplementLeadResultDialog
              leadComplementAccepted={formSchema.leadComplementAccepted}
              ownPreApproved
              leadFirstName={formSchema.complementLeadFirstName}
              alreadyEvaluated={alreadyEvaluatedInvitee}
              partnerName={formSchema.partner}
            />
          </Form>
        );
      }}
    </Formik>
  );
}
